"use client";

import {
  type ChangeEvent,
  type ChangeEventHandler,
  type InputHTMLAttributes,
  type MouseEventHandler,
  type Ref,
  useRef,
  type FC,
} from "react";
import { mergeClasses } from "@huub-web/utils";

import { BaseInputField, type BaseInputFieldProps } from "../";
import {
  inputStyle,
  placeholderStyle,
} from "../BaseInputField/BaseInputField.css";
import { typography } from "../../theme/css/typography.css";
import { mergeRefs } from "../../utils";
import { PlainTextArea } from "../PlainTextArea/PlainTextArea";
import { plainTextAreaStyle } from "./TextArea.css";
import { useFormValidation } from "../../hooks/useFormValidation";
import type { ValidatableProps } from "../../props";

export type TextAreaProps = Omit<
  BaseInputFieldProps,
  "trailingLast" | "placeholder"
> &
  ValidatableProps &
  Pick<
    InputHTMLAttributes<HTMLTextAreaElement>,
    "placeholder" | "inputMode" | "onKeyDown" | "onKeyUp"
  > & {
    onChange?: TextAreaChangeHandler;
    inputRef?: Ref<HTMLTextAreaElement>;
    value?: string;
    defaultValue?: string;
  };

export type TextAreaChangeHandler = (
  value: string,
  e: ChangeEvent<HTMLTextAreaElement>,
) => void;

/**
 * TextArea is a component that wraps around the base input field with a text input.
 */
export const TextArea: FC<TextAreaProps> = ({
  id: idProp,
  disabled: disabledProp,
  error: errorProp,
  validation,
  name,
  placeholder,
  value,
  defaultValue,
  variant = "default",
  width = "stretch",
  autoFocus,
  inputMode,
  inputRef,
  align,
  className,
  onChange,
  onClick,
  onKeyDown,
  onKeyUp,
  ref,
  ...props
}) => {
  const internalInputRef = useRef<HTMLTextAreaElement>(null);

  const { disabled, id, error } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
    ref: internalInputRef,
  });

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    internalInputRef.current?.focus();
    onClick?.(e);
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const { value } = e.target;
    onChange?.(value, e);
  };

  return (
    <BaseInputField
      ref={ref}
      className={className}
      cursor="text"
      onClick={handleClick}
      disabled={disabled}
      variant={variant}
      align={align}
      width={width}
      error={error}
      multiLine
      {...props}
    >
      <PlainTextArea
        ref={mergeRefs(internalInputRef, inputRef)}
        id={id}
        className={mergeClasses(
          typography[variant === "toggle" ? "bodyL" : "bodyM"],
          placeholderStyle,
          inputStyle({ disabled, align }),
          plainTextAreaStyle(),
        )}
        disabled={disabled}
        onChange={handleChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        value={value ?? ""}
        defaultValue={defaultValue}
        inputMode={inputMode}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      />
    </BaseInputField>
  );
};
